/**
 * dropit.fyi pricing — typed re-export of ./pricingConfig.json (single data file, mirrored at backend/pricing_config.json).
 * Stripe prices are created dynamically by lookup key `${tier.id}_${currency.toLowerCase()}_v3` on first checkout —
 * there are NO static Stripe price ids. The backend is the authority for amounts at checkout; this file only drives display.
 */
import raw from "./pricingConfig.json";

export type CurrencyCode = "NZD" | "USD" | "INR" | "ARS";
export interface CurrencyMeta { code: CurrencyCode; symbol: string; locale: string; enabled: boolean; fallback?: CurrencyCode; regions: string[] }
export interface PricingTier {
  id: "pack_starter" | "pack_trade" | "pack_pro" | "sub_solo";
  name: string;
  credits: number | "unlimited";
  type: "pack" | "subscription";
  fairUsePerMonth?: number;
  prices: Record<CurrencyCode, { amount: number; stripeLookupKey: string }>;
}

export const DEFAULT_CURRENCY = raw.defaultCurrency as CurrencyCode;
export const CURRENCIES = raw.currencies as CurrencyMeta[];
export const PRICING_CONFIG = raw.tiers as PricingTier[];
export const ENABLED_CURRENCIES = CURRENCIES.filter((c) => c.enabled);
export const FAIR_USE_PER_MONTH = PRICING_CONFIG.find((t) => t.id === "sub_solo")?.fairUsePerMonth ?? 500;

export const currencyMeta = (code: CurrencyCode): CurrencyMeta => CURRENCIES.find((c) => c.code === code) || CURRENCIES[0];

/** Apply enabled/fallback rules; unknown or disabled-without-fallback → default (NZD). */
export const resolveCurrency = (input?: string | null): CurrencyCode => {
  const code = (input || "").toUpperCase();
  const meta = CURRENCIES.find((c) => c.code === code);
  if (!meta) return DEFAULT_CURRENCY;
  if (meta.enabled) return meta.code;
  return meta.fallback ? resolveCurrency(meta.fallback) : DEFAULT_CURRENCY;
};

export const currencyForRegion = (region?: string | null): CurrencyCode | null => {
  const r = (region || "").toUpperCase();
  const meta = CURRENCIES.find((c) => c.regions.includes(r));
  return meta ? resolveCurrency(meta.code) : null;
};

/** INR: no decimals; NZD/USD: 2 dp only when non-integer. */
export const formatPrice = (amount: number, currency: CurrencyCode): string => {
  const meta = currencyMeta(currency);
  const digits = currency === "INR" || Number.isInteger(amount) ? 0 : 2;
  try {
    return new Intl.NumberFormat(meta.locale, { style: "currency", currency, currencyDisplay: "narrowSymbol", minimumFractionDigits: digits, maximumFractionDigits: digits })
      .format(amount).replace(/^(\D+)/, meta.symbol);
  } catch {
    return `${meta.symbol}${amount.toFixed(digits)}`;
  }
};

export const tierPrice = (tier: PricingTier, currency: CurrencyCode): string =>
  formatPrice(tier.prices[currency].amount, currency) + (tier.type === "subscription" ? "/mo" : "");

// --- detection: phone country → timezone → navigator.language region; unknown → default (NZD)
const TZ_COUNTRY: Record<string, string> = {
  "Pacific/Auckland": "NZ", "Pacific/Chatham": "NZ", "Asia/Kolkata": "IN", "Asia/Calcutta": "IN", "Asia/Dubai": "AE",
  "America/New_York": "US", "America/Chicago": "US", "America/Denver": "US", "America/Phoenix": "US", "America/Los_Angeles": "US",
  "America/Anchorage": "US", "Pacific/Honolulu": "US", "America/Detroit": "US", "America/Boise": "US", "America/Indiana/Indianapolis": "US",
  "America/Kentucky/Louisville": "US", "America/Juneau": "US", "America/Adak": "US",
};
export const countryFromTimeZone = (tz?: string | null): string | null => {
  if (!tz) return null;
  if (tz.startsWith("America/Argentina/")) return "AR";
  return TZ_COUNTRY[tz] || null;
};

export const detectCurrency = (opts: { phoneCountry?: string | null; timeZone?: string | null; language?: string | null } = {}): CurrencyCode => {
  const tz = opts.timeZone ?? (typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : null);
  const lang = opts.language ?? (typeof navigator !== "undefined" ? navigator.language : null);
  const langRegion = (lang || "").split("-")[1]?.slice(0, 2) || null;
  for (const region of [opts.phoneCountry, countryFromTimeZone(tz), langRegion]) {
    const c = currencyForRegion(region);
    if (c) return c;
  }
  return DEFAULT_CURRENCY;
};
